import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Dropdown, Slider } from 'antd';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import type AnimationPlayerStore from 'stores/AnimationPlayer';
import styled from 'styled-components';

import {
    LoopOffIcon,
    LoopOnIcon,
    PauseIcon,
    PlayIcon,
} from './PlayerControlsIcons';

const ControlsContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0px;
    background-color: #ffffff;
    border-radius: 8px;
    border: 1px solid #D9D9D9;
    position: relative;
    width: calc(100% - 20px);
    margin: 10px;
    overflow: hidden;
`;

const ControlButton = styled(Button)`
    font-size: 24px;
    padding: 10px;
    color: black;
    cursor: pointer;
    background: none;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;

    &.ant-btn {
        border-radius: 0;
        /* border: none; */
    }

    &.ant-btn:hover, &.ant-btn:focus {
        color: #000000;
        background-color: #fff;
        border-color: #D9D9D9;
    }

    &:hover {
        color: #595959;
    }
`;

const PlayPauseButton = styled(ControlButton)`
    border-right: 1px solid #D9D9D9;
`;

const LoopButton = styled(ControlButton)`
    border-left: 1px solid #D9D9D9;
    width: 50px;
`;

const SpeedButton = styled.div`
    cursor: pointer;
    border-left: 1px solid #d9d9d9;
    font-size: 12px;
    width: 50px;
    display: flex;
    height: 100%;
    justify-content: center;
    align-items: center;
`;

const ProgressSlider = styled(Slider)`
    flex-grow: 1;
    margin: 10px 5px;

    .ant-slider-rail {
        background-color: rgba(255, 255, 255, 0.3);
    }

    .ant-slider-track {
        background-color: #D9D9D9;
    }

    .ant-slider-handle {
        border-color: #595959 !important;
        background: #595959;
        -webkit-user-select: none;  /* Safari */
        -moz-user-select: none;     /* Firefox */
        -ms-user-select: none;      /* IE10+/Edge */
        user-select: none;          /* Standard */
        
        -webkit-user-drag: none;
        -khtml-user-drag: none;
        -moz-user-drag: none;
        -o-user-drag: none;
        /* user-drag: none;  */
    }

    .ant-slider-step {
        background-color: #D9D9D9;
        border-radius: 8px;
    }
`;

const TimeDisplay = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 50px;
    color: black;
    margin: 0 15px;
    cursor: pointer;
    user-select: none;
`;

const ScaleContainer = styled.div`
    display: flex;
    align-items: center;
    border-left: 1px solid #D9D9D9;
    height: 100%;
`;

const ScaleButton = styled(Button)`
    height: 100%;
    width: 40px;
    &.ant-btn {
        border-radius: 0;
        border: none;
    }
`;

const ScaleDisplay = styled.span`
    min-width: 70px;
    text-align: center;
    border-right: 1px solid #D9D9D9;
    border-left: 1px solid #D9D9D9;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
`;

interface PlayerControlsProps {
    player: AnimationPlayerStore;
    loaded: boolean;
}

function PlayerControls({ player, loaded }: PlayerControlsProps) {
    const isPlaying = player.isPlaying;

    const loopIcon = player.loop ? <LoopOnIcon /> : <LoopOffIcon />;
    const playPauseIcon = isPlaying ? <PauseIcon /> : <PlayIcon />;
    const frameFraction = 100 / player.totalFrames;

    const speedMenuItems = player.speedOptions.map((speed) => ({
        key: `${speed}`,
        label: `${speed}\xD7`,
        onClick: () => player.setSpeed(speed),
    }));

    const handleSliderChange = (value: number) => {
        player.setIsPlaying(false);
        if (player.instance) {
            const frame = Math.round(
                player.instance.totalFrames * (value / 100)
            );
            if (isFinite(frame)) player.instance.goToAndStop(frame, true);
        }
    };

    useEffect(() => {
        const onKey = (keyEvent: KeyboardEvent) => {
            if (keyEvent.key === ' ') {
                player.togglePlayPause();
            }
        };

        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('keydown', onKey);
        };
    }, [player]);

    return (
        <ControlsContainer>
            <PlayPauseButton onClick={player.togglePlayPause}>
                {playPauseIcon}
            </PlayPauseButton>

            <TimeDisplay onClick={player.toggleTimeDisplay}>
                {player.currentTimeDisplay}
            </TimeDisplay>

            <ProgressSlider
                value={player.progress}
                onChange={handleSliderChange}
                tooltip={{
                    formatter: (_ignored) => player.currentTimeDisplay,
                    placement: 'top',
                }}
                min={0}
                max={100}
                step={frameFraction}
                disabled={!loaded}
                onChangeComplete={() => {
                    player.setIsPlaying(false);
                }}
            />

            <TimeDisplay onClick={player.toggleTimeDisplay}>
                {player.durationDisplay}
            </TimeDisplay>

            <LoopButton onClick={player.toggleLoop}>{loopIcon}</LoopButton>

            <Dropdown
                menu={{ items: speedMenuItems }}
                placement="top"
                trigger={['click']}
            >
                <SpeedButton>{player.speed}&#xD7;</SpeedButton>
            </Dropdown>

            <ScaleContainer>
                <ScaleButton
                    icon={<MinusOutlined />}
                    onClick={player.decreaseScale}
                    disabled={player.scale <= player.minScale}
                />
                <ScaleDisplay>{player.scalePercentage}</ScaleDisplay>
                <ScaleButton
                    icon={<PlusOutlined />}
                    onClick={player.increaseScale}
                    disabled={player.scale >= player.maxScale}
                />
            </ScaleContainer>
        </ControlsContainer>
    );
}

export default observer(PlayerControls);
